import { DialogWithForm } from '@strata/react-dialog';
import { FileUploadFormik } from '@strata/react-file-upload';
import { TextAreaFormik } from '@strata/react-inputs';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { putRequestChanges } from 'api/clinicalPolicyAPI';
import { handleError } from 'services/Error';
import { downloadPolicyFile, removePolicyFile, uploadPolicyFiles } from 'services/Files';
import { useAppSelector } from 'store';
import { type PolicySummary } from 'store/api/clinicalPolicyApi';

const ValidationSchema = Yup.object({
    changeDescription: Yup.string().required(validationStrings.requiredField.errorMessage),
    attachments: FileUploadFormik.validator(),
});

interface RequestChangesDialogProps {
    isOpen: boolean;
    onSecondaryButtonClick: () => void;
    policyData?: PolicySummary;
}

export const RequestChangesDialog = ({
    isOpen,
    onSecondaryButtonClick,
    policyData = undefined,
}: RequestChangesDialogProps) => {
    const { payerId } = useAppSelector((state) => state.auth);
    return policyData ? (
        <Formik
            initialValues={{} as Yup.InferType<typeof ValidationSchema>}
            enableReinitialize
            validationSchema={ValidationSchema}
            onSubmit={async (values, { resetForm }) => {
                try {
                    await putRequestChanges({
                        payerId: payerId!,
                        draftId: policyData.draftId,
                        change: values.changeDescription,
                        attachments: values.attachments?.map((file) => file.name),
                    });
                    resetForm();
                    onSecondaryButtonClick();
                } catch (err) {
                    handleError('An error occurred requesting policy changes.', err as Error);
                }
            }}
        >
            {(formik) => (
                <DialogWithForm
                    dialogTitle="Request Changes"
                    isOpen={isOpen}
                    primaryButtonLabel="Send Request"
                    isPrimaryButtonDisabled={!formik.isValid || formik.isSubmitting}
                    secondaryButtonLabel="Cancel"
                    onSecondaryButtonClick={(e) => {
                        formik.handleReset(e);
                        onSecondaryButtonClick();
                    }}
                    dialogContent={
                        <>
                            <p className="margin__bottom--large">
                                You can request changes from the Free Market Health team. Once the
                                changes are completed, this policy will be sent by Free Market
                                Health for another review.
                            </p>
                            <TextAreaFormik
                                id="changeDescription"
                                label="Change Description"
                                required
                            />
                            <FileUploadFormik
                                id="attachments"
                                label="Additional Clinical Documents"
                                onAddFiles={(files) =>
                                    uploadPolicyFiles({
                                        payerId: payerId!,
                                        draftId: policyData?.draftId,
                                        files,
                                    })
                                }
                                onRemoveFile={(filename) =>
                                    removePolicyFile({
                                        payerId: payerId!,
                                        draftId: policyData?.draftId,
                                        filename,
                                    })
                                }
                                onFileClick={(filename) => {
                                    void downloadPolicyFile({
                                        payerId: payerId!,
                                        draftId: policyData?.draftId,
                                        filename,
                                    });
                                }}
                            />
                        </>
                    }
                />
            )}
        </Formik>
    ) : null;
};
